// US time zones for interview scheduling (staffing is US-based).
export const US_TIMEZONES = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HT)" },
];

export const DEFAULT_TZ = "America/New_York";

// Convert a wall-clock datetime-local string ("2026-08-20T10:00"), interpreted
// as a time in `tz`, into a UTC ISO instant. DST-safe via offset lookup.
export function wallToUTCISO(wall, tz) {
  if (!wall) return null;
  const asUTC = new Date(`${wall}:00Z`).getTime();
  if (isNaN(asUTC)) return null;
  const shown = new Date(new Date(asUTC).toLocaleString("en-US", { timeZone: tz })).getTime();
  const utcShown = new Date(new Date(asUTC).toLocaleString("en-US", { timeZone: "UTC" })).getTime();
  const offset = shown - utcShown;
  return new Date(asUTC - offset).toISOString();
}

// Convert a UTC ISO instant into a datetime-local wall string as it appears in `tz`.
export function isoToWallInput(iso, tz) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d);
  const p = {};
  parts.forEach((x) => { p[x.type] = x.value; });
  const hour = p.hour === "24" ? "00" : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
}

// Format a UTC ISO instant for display in `tz`, including the zone abbreviation.
export function fmtInTz(iso, tz) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: tz || undefined, weekday: "short", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", timeZoneName: tz ? "short" : undefined,
    });
  } catch {
    return new Date(iso).toLocaleString("en-US");
  }
}

export function tzLabel(value) {
  return US_TIMEZONES.find((t) => t.value === value)?.label || value;
}

// The candidate's own browser time zone (IANA).
export function localTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ""; }
}

// Short local-time string with zone abbreviation, e.g. "11:30 PM IST".
export function localTimeShort(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", { hour: "2-digit", minute: "2-digit", timeZoneName: "short" });
}

// True when the interview zone shows a different wall time than the viewer's local zone.
export function differsFromLocal(iso, tz) {
  const local = localTz();
  if (!iso || !tz || tz === local) return false;
  const opts = { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
  return (
    new Date(iso).toLocaleString("en-US", { ...opts, timeZone: tz }) !==
    new Date(iso).toLocaleString("en-US", { ...opts, timeZone: local })
  );
}

// Given a weekday (0=Sun..6=Sat) and a "HH:mm" time in `tz`, return the datetime-local
// wall string for the NEXT upcoming occurrence, skipping any date in `blackouts` (["YYYY-MM-DD"]).
export function nextWallForWeekday(weekday, time, tz, blackouts = []) {
  const now = new Date();
  const parts = {};
  new Intl.DateTimeFormat("en-US", {
    timeZone: tz, weekday: "short", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(now).forEach((p) => { parts[p.type] = p.value; });
  const wdMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const curWd = wdMap[parts.weekday];
  const curHour = parts.hour === "24" ? 0 : Number(parts.hour);
  const curMin = Number(parts.minute);
  const [th, tm] = (time || "10:00").split(":").map(Number);
  let days = (Number(weekday) - curWd + 7) % 7;
  if (days === 0 && (th < curHour || (th === curHour && tm <= curMin))) days = 7;
  const base = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
  base.setUTCDate(base.getUTCDate() + days);
  const p2 = (n) => String(n).padStart(2, "0");
  const blocked = new Set(blackouts || []);
  for (let guard = 0; guard < 12; guard++) {
    const dateStr = `${base.getUTCFullYear()}-${p2(base.getUTCMonth() + 1)}-${p2(base.getUTCDate())}`;
    if (!blocked.has(dateStr)) return `${dateStr}T${p2(th)}:${p2(tm)}`;
    base.setUTCDate(base.getUTCDate() + 7); // date is blacked out — roll to next week
  }
  return "";
}
